import { z } from 'zod';
import { AuthTokens } from './auth.js';
import { HttpUrl } from './common/url.js';
/**
 * Social sign-in (FR-AUTH-01, FR-AUTH-02, US-AUTH-01/02, AC-AUTH-01).
 *
 * Kept in its own file rather than folded into auth.ts because the providers
 * are a bounded domain of their own: adding a fifth one touches these schemas
 * and one verifier, and nothing that email or OTP sign-in depends on.
 */
/**
 * `instagram` is declared but not servable today (`Q-09`): Instagram Basic
 * Display performs no authentication, so a standalone "Continue with
 * Instagram" needs Facebook Login for Business and an Instagram Professional
 * account on the member's side. The provider is named here so the shape is
 * settled; whether the API accepts it is decided by configuration, which is
 * where a provider without credentials belongs.
 */
export const SOCIAL_PROVIDERS = ['google', 'facebook', 'instagram', 'apple'];
export const SocialProvider = z.enum(SOCIAL_PROVIDERS);
/**
 * The token exchange (ADR-20 style: one contract, both ends).
 *
 * The client performs the consent step natively and sends what the provider
 * gave it — an OIDC id token for Google and Apple, an access token for
 * Facebook and Instagram. The API never sees the member's provider password:
 * it verifies, reads the identity, and issues an ONOV session, which is the
 * whole of the exchange.
 *
 * With ONE exception, added deliberately and kept as narrow as it can be:
 * Apple's refresh token is retained when `authorizationCode` is supplied,
 * because Apple requires it to be revoked on account deletion and there is no
 * other moment at which it can be obtained. It is used for that call and
 * nothing else, and it dies with the identity row.
 */
export const SocialLoginInput = z.object({
    /** id token (google, apple) or access token (facebook, instagram). */
    token: z.string().min(1).max(8_192),
    /**
     * Replay guard for the OIDC providers. When present it must equal the nonce
     * the client put in the authorize request, and the verifier refuses the
     * token if the claim disagrees. Absent is allowed — Facebook's access token
     * has no nonce to compare — but a client that can send one should.
     */
    nonce: z.string().min(1).max(256).optional(),
    /**
     * Apple's one-time authorization code from the same consent, sent so the
     * API can obtain a refresh token and later REVOKE it.
     *
     * Apple requires an app that offers Sign in with Apple to revoke the
     * member's tokens when they delete their account, and revocation takes a
     * token we can only get by exchanging this code — the id token cannot be
     * revoked and the code is spent within minutes of consent. So it has to be
     * collected at sign-in, long before anyone asks to be deleted.
     *
     * Optional, and ignored by every other provider. A client that does not send
     * it still signs in exactly as before; the account simply deletes without a
     * revocation call, which `AccountDeleted.revoked` reports honestly rather
     * than by omission.
     */
    authorizationCode: z.string().min(1).max(2_048).optional(),
    /**
     * The name Apple handed the CLIENT at first consent, sent up so the API can
     * keep it — the only way this value can ever reach a server.
     *
     * Apple discloses a name exactly once: in the authorization response of the
     * first consent for this Apple ID and app, never in the id token and never
     * again on any later sign-in. So it has the same shape of problem as
     * `authorizationCode` above and the same answer — collect it at the one
     * moment it exists, long before anything needs it, or do without it forever.
     *
     * Optional, and ignored by every other provider: Google and Facebook put a
     * name in every token they issue, so there is nothing here for them to
     * rescue.
     *
     * UNVERIFIED, and treated as such. Unlike everything else in this request it
     * is not covered by a provider signature — the member may edit it on Apple's
     * consent sheet, and a hostile client may simply invent it. It is therefore
     * only ever a greeting and a prefill, never an identity: linking keys on
     * `sub`, and the member's own profile name outranks it the moment they have
     * one.
     */
    displayName: z.string().trim().min(1).max(120).optional(),
});
/**
 * What the provider knew about the member, passed through for onboarding
 * (AC-AUTH-01.1) and never persisted by this flow. The member owns their
 * profile; a name and a picture the provider volunteered are a starting value
 * for a form, not a record to write behind their back.
 *
 * `avatarUrl` is `HttpUrl`, not `z.string().url()`. It is an external string
 * that ends up in an `<Image src>` — the same reason the member-supplied urls
 * were narrowed to http(s).
 */
export const SocialPrefill = z.object({
    displayName: z.string().min(1).max(120).optional(),
    avatarUrl: HttpUrl.optional(),
});
export const SocialLoginResult = AuthTokens.extend({
    /**
     * True only when this call created the account. A returning member goes
     * straight to the app while a new member enters onboarding.
     */
    isNewAccount: z.boolean(),
    /**
     * The member's name on file, sent for APPLE sign-ins only.
     *
     * Apple's id token carries no name, and Apple discloses one exactly once —
     * to the client, on the FIRST consent, never again. So a returning Apple
     * member's sign-in response would otherwise be nameless, and a client that
     * wanted one would have had to bank it from a single consent and keep it
     * forever. The API answers instead, from the profile it already holds.
     *
     * The other providers are deliberately absent from this field rather than
     * served by it: they put their own name in `prefill` on every sign-in, so
     * for them this would be a second spelling of a value the response already
     * carries.
     *
     * Not the same thing as `prefill.displayName` even when both appear. This is
     * what ONOV calls the member; `prefill` is what the PROVIDER offered to
     * start their profile with, and the two agree only until the member edits
     * their profile.
     *
     * Optional, because it can genuinely be neither: a first Apple consent on an
     * account with no profile yet knows no name at all, and deriving one from
     * the email local-part would put a guess where the member's own name goes.
     */
    displayName: z.string().min(1).max(120).optional(),
    /** Absent when the provider volunteered neither a name nor a picture. */
    prefill: SocialPrefill.optional(),
});
/** Which providers this deployment can actually serve — see `Q-09` above. */
export const SocialProvidersAvailable = z.object({
    providers: z.array(SocialProvider),
});
//# sourceMappingURL=social-auth.js.map